import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Divider, Input, InputRef, Modal, Row, Space } from "antd";
import { ColumnsType, ColumnType } from "antd/lib/table";
import { SearchOutlined } from "@ant-design/icons";
import { FilterConfirmProps } from "antd/lib/table/interface";
import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { AlertType, useAlert } from "../provider/AlertProvider";

import DataTable from "./DataTable";
import RentRegisterFap from "./RentRegisterFap";
import Highlighter from "react-highlight-words";

import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as String from "../commons/string";

type DataIndex = keyof Types.RentFaqDto;

function RentFaqList() {
    const alert = useAlert();

    const [faqs, setFaqs] = useState<Array<Types.RentFaqDto>>();
    const [currentFaq, setcurrentFaq] = useState<Types.RentFaqDto>();

    const [editMode, setEditMode] = useState<"register" | "update">("update");
    const [openDeleteFaq, setOpenDeleteFaq] = useState(false);

    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const searchInput = useRef<InputRef>(null);

    const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: DataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };

    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<Types.RentFaqDto> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        {String.search}
                    </Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        {String.reset}
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        {String.filter}
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        {String.close}
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ) : (
                text
            ),
    });

    const columns: ColumnsType<Types.RentFaqDto> = [
        {
            align: "center",
            title: String.faqId,
            dataIndex: "rentFaqId",
            key: "rentFaqId",
            ...getColumnSearchProps("rentFaqId"),
        },
        {
            align: "center",
            title: String.faqQuestion,
            dataIndex: "faqQuestion",
            key: "faqQuestion",
            ...getColumnSearchProps("faqQuestion"),
        },
        {
            align: "center",
            title: String.faqOrder,
            dataIndex: "faqOrder",
            key: "faqOrder",
            ...getColumnSearchProps("faqOrder"),
        },
    ];

    const requestAxiosGetFaqs = async () => {
        const response = await utilAxiosWithAuth().get(Request.RENT_FAQ_LIST_URL);
        console.log(response.data);
        return response.data;
    };

    const { loading: loadingGetFaqs, error: errorGetFaqs, data: resultGetFaqs, execute: requestGetFaqs } = useAsyncAxios(requestAxiosGetFaqs);

    useEffect(() => {
        if (resultGetFaqs === null) return;
        console.log("resultGetFaqs", resultGetFaqs);

        setFaqs(resultGetFaqs.rentFaqs);
    }, [resultGetFaqs]);

    useEffect(() => {
        if (errorGetFaqs === null) return;

        //console.log("errorGetUsers", errorGetUsers);
        alert.setAlert(errorGetFaqs.response.data.code, "Error", errorGetFaqs.response.data.message);
    }, [errorGetFaqs]);

    const requestAxiosRegisterFaq = async (faq: Types.RentFaqDto) => {
        const response = await utilAxiosWithAuth().post(Request.RENT_FAQ_URL, faq);
        console.log(response.data);
        return response.data;
    };

    const {
        loading: loadingRegisterFaq,
        error: errorRegisterFaq,
        data: resultRegisterFaq,
        execute: requestRegisterFaq,
    } = useAsyncAxios(requestAxiosRegisterFaq);

    useEffect(() => {
        if (resultRegisterFaq === null) return;
        console.log("resultRegisterFaq", resultUpdateFaq);
        alert.setAlert(AlertType.SUCCESS, "FAQ 등록 성공", "FAQ가 등록되었습니다.");

        setEditMode("update");
        requestGetFaqs();
        setcurrentFaq(resultRegisterFaq.rentFaq);
    }, [resultRegisterFaq]);

    useEffect(() => {
        if (errorRegisterFaq === null) return;

        console.log("errorRegisterFaq", errorRegisterFaq);
        alert.setAlert(errorRegisterFaq.response.data.code, "Error", errorRegisterFaq.response.data.message);
    }, [errorRegisterFaq]);

    const requestAxiosUpdateFaq = async (faq: Types.RentFaqDto) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_FAQ_URL + "/" + faq.rentFaqId, faq);
        console.log(response.data);
        return response.data;
    };

    const { loading: loadingUpdateFaq, error: errorUpdateFaq, data: resultUpdateFaq, execute: requestUpdateFaq } = useAsyncAxios(requestAxiosUpdateFaq);

    useEffect(() => {
        if (resultUpdateFaq === null) return;
        console.log("resultUpdateFaq", resultUpdateFaq);
        alert.setAlert(AlertType.SUCCESS, "FAQ 수정 성공", "FAQ가 수정되었습니다.");

        setEditMode("update");
        setcurrentFaq(resultUpdateFaq.rentFaq);
        requestGetFaqs();
    }, [resultUpdateFaq]);

    useEffect(() => {
        if (errorUpdateFaq === null) return;

        //console.log("errorUpdateFaq", errorUpdateFaq);
        alert.setAlert(errorUpdateFaq.response.data.code, "Error", errorUpdateFaq.response.data.message);
    }, [errorUpdateFaq]);

    const requestAxiosRemoveFaq = async (faq: Types.RentFaqDto) => {
        const response = await utilAxiosWithAuth().delete(Request.RENT_FAQ_URL + "/" + faq?.rentFaqId);
        return response.data;
    };

    const { loading: loadingRemoveFaq, error: errorRemoveFaq, data: resultRemoveFaq, execute: requestRemoveFaqs } = useAsyncAxios(requestAxiosRemoveFaq);

    useEffect(() => {
        if (resultRemoveFaq === null) return;
        console.log("resultRemoveFaq", resultRemoveFaq);
        alert.setAlert(AlertType.SUCCESS, "FAQ 삭제 성공", "FAQ 삭제를 완료하였습니다.");

        setEditMode("register");
        setcurrentFaq(undefined);
        requestGetFaqs();
    }, [resultRemoveFaq]);

    useEffect(() => {
        if (errorRemoveFaq === null) return;

        alert.setAlert(errorRemoveFaq.response.data.code, "Error", errorRemoveFaq.response.data.message);
    }, [errorGetFaqs]);

    useEffect(() => {
        requestGetFaqs();
    }, []);

    const onRow = (value: Types.RentFaqDto) => {
        setEditMode("update");
        setcurrentFaq(value);
    };

    const handleDeleteFaq = () => {
        if (currentFaq) setOpenDeleteFaq(true);
    };

    const handleDeleteFaqOk = () => {
        setOpenDeleteFaq(false);
        requestRemoveFaqs(currentFaq);
    };

    const handleDeleteFaqCancel = () => {
        setOpenDeleteFaq(false);
    };

    const onResult = (faq: Types.RentFaqDto) => {
        editMode === "register" ? requestRegisterFaq(faq) : requestUpdateFaq(faq);
    };

    return (
        <div style={{ display: "flex", flexDirection: "column" }}>
            <Row justify={"space-between"}>
                <Col span={6}>
                    <Divider orientation="left">
                        <span>FAQ</span>
                    </Divider>
                </Col>
                <Space>
                    <Button
                        onClick={() => {
                            setEditMode(editMode === "register" ? "update" : "register");
                        }}
                    >
                        {String.userAdd}
                    </Button>
                    <Button onClick={handleDeleteFaq} danger>
                        {String.userRemove}
                    </Button>
                </Space>
            </Row>
            <Space size={20} direction="vertical" style={{ width: "100%" }}>
                <DataTable rowKey={(row: Types.RentFaqDto) => row.rentFaqId} hasScroll={false} columns={columns} dataSource={faqs} onRow={onRow} />
                <RentRegisterFap editMode={editMode} faq={currentFaq} onResult={onResult} />
            </Space>
            <Modal open={openDeleteFaq} onOk={handleDeleteFaqOk} onCancel={handleDeleteFaqCancel}>
                {currentFaq?.faqQuestion} 삭제하시겠습니까?
            </Modal>
        </div>
    );
}

export default RentFaqList;
